import type { CreateTrailerInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, InputNumber, Select, Switch } from 'antd';
import { useTrailerTypeOptions } from './use-trailer-type-options';

interface CreateTrailerDrawerProps {
  open: boolean;
  error: string | null;
  submitting: boolean;
  onCreate: (input: CreateTrailerInput) => Promise<boolean>;
  onClose: () => void;
}

/** Registration, type, capacity, axle count and the three capability flags — see gitaumoses4/chukua#13. */
export function CreateTrailerDrawer({ open, error, submitting, onCreate, onClose }: CreateTrailerDrawerProps) {
  const [form] = Form.useForm<CreateTrailerInput>();
  const typeOptions = useTrailerTypeOptions();

  async function submit(values: CreateTrailerInput) {
    const created = await onCreate(values);
    if (created) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Create trailer" placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<CreateTrailerInput>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={submit}
        disabled={submitting}
        initialValues={{ isSkeletal: false, isFlatRack: false, isReefer: false }}
      >
        <Form.Item name="registration" label="Registration" rules={[{ required: true, message: 'Enter a registration' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="typeCode" label="Trailer type" rules={[{ required: true, message: 'Select a trailer type' }]}>
          <Select
            options={typeOptions.map((type) => ({ value: type.code, label: type.name }))}
            placeholder="Select a trailer type"
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <div className="grid grid-cols-2 gap-3">
          <Form.Item name="capacityTonnes" label="Capacity (tonnes)" rules={[{ required: true, message: 'Enter a capacity' }]}>
            <InputNumber className="w-full" min={0} />
          </Form.Item>
          <Form.Item name="axleCount" label="Axle count" rules={[{ required: true, message: 'Enter an axle count' }]}>
            <InputNumber className="w-full" min={1} precision={0} />
          </Form.Item>
        </div>
        <Form.Item name="isSkeletal" label="Skeletal" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="isFlatRack" label="Flat rack" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="isReefer" label="Reefer" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Create trailer
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
